import React, { useState } from 'react';
import { HowToPage } from './components/HowToPage';
import { AIProgramPage } from './components/AIProgramPage';
import { AppState } from './types';
import { WaterScene } from './components/WaterScene';
import { ControlPanel } from './components/ControlPanel';
export function App() {
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [showHowTo, setShowHowTo] = useState(false);
  const [showAIProgram, setShowAIProgram] = useState(false);
  const [zoomLevel, setZoomLevel] = useState(2.6);
  const [presets, setPresets] = useState<(AppState | null)[]>(Array(10).fill(null));
  const [activePreset, setActivePreset] = useState<number | null>(null);
  const handlePresetClick = (index: number) => {
    if (presets[index]) {
      // Load saved preset
      setState(presets[index]!);
      setActivePreset(index);
    } else {
      // Save current state to this slot
      const newPresets = [...presets];
      newPresets[index] = {
        ...state
      };
      setPresets(newPresets);
      setActivePreset(index);
    }
  };
  const [state, setState] = useState<AppState>({
    pattern: 'Gentle Sway',
    speed: 2.9,
    swingAngle: 45,
    sweepJerkiness: 0,
    pauseAtTop: false,
    pauseAngle: 92,
    pauseDuration: 0,
    jigAtPause: false,
    jigDistance: 3,
    jigSpeedUp: 2,
    jigPauseBetween: 0,
    jigSpeedDown: 2,
    interval: 'Continuous',
    isRunning: true,
    bobberStopDepth: 6,
    lureType: 'Shad',
    lureColor: '#7fff00',
    lureSize: 'M',
    armatureSize: 'XL',
    pauseCountdown: 0
  });
  return <div className="flex flex-col w-full h-screen bg-[#050b14] text-white font-sans overflow-hidden" data-id="element-0">
      {/* Top Bar - Menu */}
      <header className={`h-12 bg-gradient-to-r from-[#0c1a2e] via-[#1a2a40] to-[#2a3a50] border-b border-white/10 flex items-center justify-between px-6 shrink-0 z-20 relative ${isFullScreen ? 'hidden' : ''}`} data-id="element-1">
        <h1 className="text-lg font-extrabold tracking-wider text-[#2d8fd5] uppercase" data-id="element-2">
          BaitMover Pro
        </h1>
        <nav className="flex items-center gap-6" data-id="element-3">
          <a href="http://www.baitmoverpro.com" target="_blank" rel="noopener noreferrer" className="text-sm font-medium text-white/80 hover:text-white tracking-wider uppercase transition-colors" data-id="element-4">
            Home
          </a>
          <button onClick={() => setShowHowTo(true)} className="text-sm font-medium text-white/80 hover:text-white tracking-wider uppercase transition-colors" data-id="element-5">
            How To
          </button>
        </nav>
      </header>

      {/* How To Page */}
      {showHowTo && <div className="flex-1 overflow-hidden" data-id="element-6">
          <HowToPage onClose={() => setShowHowTo(false)} data-id="element-7" />
        </div>}

      {/* Main Content Area */}
      <main className={`flex-1 flex overflow-hidden relative bg-gradient-to-br from-[#0a1628] to-[#050b14] ${showHowTo || showAIProgram ? 'hidden' : ''}`} data-id="element-8">
        {/* Left Side - Full Screen Visualization */}
        <div className={`flex-1 relative overflow-hidden ${isFullScreen ? '' : 'hidden'}`} data-id="element-9">
          <WaterScene state={state} zoomLevel={zoomLevel} instanceId="fullscreen-main" lureSelectorPosition="top" onLureColorChange={lureColor => setState(s => ({
          ...s,
          lureColor
        }))} onLureSizeChange={lureSize => setState(s => ({
          ...s,
          lureSize
        }))} onArmatureSizeChange={armatureSize => setState(s => ({
          ...s,
          armatureSize
        }))} onPauseCountdownChange={pauseCountdown => setState(s => ({
          ...s,
          pauseCountdown
        }))} onBobberStopDepthChange={bobberStopDepth => setState(s => ({
          ...s,
          bobberStopDepth
        }))} presets={presets} activePreset={activePreset} onPresetPlay={i => {
          if (presets[i]) {
            setState(presets[i]!);
            setActivePreset(i);
          }
        }} data-id="element-10" />

          {/* Minimize Screen Icon - Top Right */}
          <button onClick={() => setIsFullScreen(false)} className="absolute top-16 right-4 z-40 w-10 h-10 flex items-center justify-center rounded-lg bg-white/90 backdrop-blur-sm border border-gray-200 text-gray-900 shadow-md hover:bg-white active:scale-95 transition-all" data-id="element-11">
            <svg width="18" height="18" viewBox="0 0 16 16" fill="none" data-id="element-12">
              <path d="M5 1V5H1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-13" />
              <path d="M11 15V11H15" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-14" />
              <path d="M11 1V5H15" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-15" />
              <path d="M5 15V11H1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-16" />
            </svg>
          </button>
        </div>

        {/* Right Side - iPhone Frame */}
        <div className={`shrink-0 relative z-10 flex items-center justify-center py-3 ${isFullScreen ? 'hidden' : 'flex-1'}`} data-id="element-17">
          {/* iPhone Shell */}
          <div className="relative w-[348px] h-full max-h-[628px] bg-black rounded-[40px] p-[8px] shadow-[0_0_60px_rgba(0,0,0,0.8),inset_0_0_2px_rgba(255,255,255,0.1)]" style={{
          border: '2px solid #2a2a2e',
          backgroundImage: 'linear-gradient(145deg, #1a1a1e 0%, #0a0a0c 50%, #1a1a1e 100%)'
        }} data-id="element-18">
            {/* Side buttons — volume */}
            <div className="absolute -left-[3px] top-[108px] w-[3px] h-[22px] bg-[#2a2a2e] rounded-l-sm" data-id="element-19" />
            <div className="absolute -left-[3px] top-[139px] w-[3px] h-[40px] bg-[#2a2a2e] rounded-l-sm" data-id="element-20" />
            <div className="absolute -left-[3px] top-[186px] w-[3px] h-[40px] bg-[#2a2a2e] rounded-l-sm" data-id="element-21" />
            {/* Side button — power */}
            <div className="absolute -right-[3px] top-[155px] w-[3px] h-[56px] bg-[#2a2a2e] rounded-r-sm" data-id="element-22" />

            {/* Screen area */}
            <div className="relative w-full h-full bg-[#F2F2F7] rounded-[32px] overflow-hidden flex flex-col" data-id="element-23">
              {/* Status Bar */}
              <div className="relative h-[28px] shrink-0 bg-[#F2F2F7] flex items-end justify-between px-6 pb-1 z-20" data-id="element-24">
                {/* Dynamic Island */}
                <div className="absolute top-[10px] left-1/2 -translate-x-1/2 w-[70px] h-[20px] bg-black rounded-full z-30" data-id="element-25" />
                {/* Time */}
                <span className="text-[12px] font-semibold text-black" style={{
                fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", system-ui, sans-serif'
              }} data-id="element-26">
                  9:41
                </span>
                {/* Status icons */}
                <div className="flex items-center gap-1.5" data-id="element-27">
                  <svg width="17" height="12" viewBox="0 0 17 12" fill="none" data-id="element-28">
                    <rect x="0" y="3" width="3" height="9" rx="1" fill="black" data-id="element-29" />
                    <rect x="4.5" y="2" width="3" height="10" rx="1" fill="black" data-id="element-30" />
                    <rect x="9" y="0.5" width="3" height="11.5" rx="1" fill="black" data-id="element-31" />
                    <rect x="13.5" y="0" width="3" height="12" rx="1" fill="black" data-id="element-32" />
                  </svg>
                  <svg width="16" height="12" viewBox="0 0 16 12" fill="none" data-id="element-33">
                    <path d="M8 2.4C10.6 2.4 12.9 3.5 14.5 5.3L15.5 4.2C13.6 2.1 11 0.8 8 0.8C5 0.8 2.4 2.1 0.5 4.2L1.5 5.3C3.1 3.5 5.4 2.4 8 2.4Z" fill="black" data-id="element-34" />
                    <path d="M8 6C9.8 6 11.4 6.7 12.5 7.9L13.5 6.8C12.1 5.3 10.2 4.4 8 4.4C5.8 4.4 3.9 5.3 2.5 6.8L3.5 7.9C4.6 6.7 6.2 6 8 6Z" fill="black" data-id="element-35" />
                    <circle cx="8" cy="10.5" r="1.5" fill="black" data-id="element-36" />
                  </svg>
                  <div className="flex items-center gap-0.5" data-id="element-37">
                    <div className="w-[22px] h-[11px] border border-black rounded-[3px] relative p-[1.5px]" data-id="element-38">
                      <div className="h-full w-[75%] bg-black rounded-[1.5px]" data-id="element-39" />
                    </div>
                    <div className="w-[1.5px] h-[5px] bg-black rounded-r-sm" data-id="element-40" />
                  </div>
                </div>
              </div>

              {/* App Content — Conditional View */}
              <div className="flex-1 overflow-hidden flex flex-col relative" data-id="element-41">
                {isFullScreen /* Full Screen Bobber View */ ? <>
                    <div className="flex-1 relative" data-id="element-42">
                      <WaterScene state={state} zoomLevel={zoomLevel} instanceId="fullscreen" lureSelectorPosition="top" onLureColorChange={lureColor => setState(s => ({
                    ...s,
                    lureColor
                  }))} onLureSizeChange={lureSize => setState(s => ({
                    ...s,
                    lureSize
                  }))} onArmatureSizeChange={armatureSize => setState(s => ({
                    ...s,
                    armatureSize
                  }))} onPauseCountdownChange={pauseCountdown => setState(s => ({
                    ...s,
                    pauseCountdown
                  }))} onBobberStopDepthChange={bobberStopDepth => setState(s => ({
                    ...s,
                    bobberStopDepth
                  }))} data-id="element-43" />

                      {/* Zoom Slider - Left Side */}
                      <div className="absolute left-1 top-1/3 -translate-y-1/2 z-40 bg-white/90 backdrop-blur-sm rounded-full px-0.5 py-1.5 shadow-md border border-gray-200" data-id="element-44">
                        <div className="flex flex-col items-center gap-1" data-id="element-45">
                          {/* Zoom In Button */}
                          <button onClick={() => setZoomLevel(Math.min(3, zoomLevel + 0.25))} className="w-4 h-4 flex items-center justify-center rounded-full bg-[#1e40af] text-white font-bold text-[9px] hover:bg-[#1e40af]/80 active:scale-95 transition-all" data-id="element-46">
                            +
                          </button>

                          {/* Vertical Slider */}
                          <input type="range" min="0.5" max="3" step="0.1" value={zoomLevel} onChange={e => setZoomLevel(parseFloat(e.target.value))} className="w-0.5 h-16 appearance-none bg-gray-300 rounded-full cursor-pointer" style={{
                        writingMode: 'bt-lr',
                        WebkitAppearance: 'slider-vertical'
                      }} data-id="element-47" />

                          {/* Zoom Out Button */}
                          <button onClick={() => setZoomLevel(Math.max(0.5, zoomLevel - 0.25))} className="w-4 h-4 flex items-center justify-center rounded-full bg-[#1e40af] text-white font-bold text-[9px] hover:bg-[#1e40af]/80 active:scale-95 transition-all" data-id="element-48">
                            −
                          </button>

                          {/* Zoom Level Indicator */}
                          <div className="text-[6px] font-mono font-bold text-gray-700 mt-0.5" data-id="element-49">
                            {Math.round(zoomLevel * 100)}%
                          </div>
                        </div>
                      </div>
                      {/* Control Panel - Top */}
                      <div className="absolute top-2 left-1/2 -translate-x-1/2 z-40 w-[90%] max-w-md max-h-[60vh] overflow-auto" data-id="element-50">
                        <ControlPanel state={state} setState={setState} onToggleFullScreen={() => {
                      setIsFullScreen(false);
                    }} zoomLevel={zoomLevel} data-id="element-51" />
                      </div>

                      {/* Return to Control Panel Button */}
                      <div className="absolute top-4 right-4 z-40" data-id="element-52">
                        <button onClick={() => setIsFullScreen(false)} className="px-2 py-1 font-bold text-[7px] uppercase tracking-wide flex items-center gap-1.5 transition-all active:scale-[0.98] rounded-lg bg-white border border-gray-200 text-gray-900 shadow-sm font-mono" data-id="element-53">
                          {/* Tiny iPhone icon */}
                          <svg width="12" height="16" viewBox="0 0 12 16" fill="none" data-id="element-54">
                            {/* iPhone outline */}
                            <rect x="0.5" y="0.5" width="11" height="15" rx="1.5" stroke="#1e40af" strokeWidth="0.5" fill="white" data-id="element-55" />
                            {/* Dynamic Island */}
                            <rect x="3.5" y="1.5" width="5" height="1.5" rx="0.75" fill="#000000" data-id="element-56" />
                            {/* Screen content lines (representing control panel) */}
                            <line x1="2" y1="4" x2="10" y2="4" stroke="#1e40af" strokeWidth="0.3" data-id="element-57" />
                            <line x1="2" y1="5.5" x2="10" y2="5.5" stroke="#1e40af" strokeWidth="0.3" data-id="element-58" />
                            <line x1="2" y1="7" x2="10" y2="7" stroke="#1e40af" strokeWidth="0.3" data-id="element-59" />
                            <line x1="2" y1="8.5" x2="10" y2="8.5" stroke="#1e40af" strokeWidth="0.3" data-id="element-60" />
                            <line x1="2" y1="10" x2="10" y2="10" stroke="#1e40af" strokeWidth="0.3" data-id="element-61" />
                            {/* Home indicator */}
                            <rect x="4" y="13.5" width="4" height="0.5" rx="0.25" fill="#000000" opacity="0.3" data-id="element-62" />
                          </svg>
                          Return to Controls
                        </button>
                      </div>
                    </div>
                  </> /* Split View */ : <>
                    {/* Bobber Scene - Top */}
                    <div className="flex-1 overflow-hidden relative bg-[#1a5276]" data-id="element-63">
                      <WaterScene state={state} zoomLevel={0.85} instanceId="iphone" onLureColorChange={lureColor => setState(s => ({
                    ...s,
                    lureColor
                  }))} onLureSizeChange={lureSize => setState(s => ({
                    ...s,
                    lureSize
                  }))} onArmatureSizeChange={armatureSize => setState(s => ({
                    ...s,
                    armatureSize
                  }))} onPauseCountdownChange={pauseCountdown => setState(s => ({
                    ...s,
                    pauseCountdown
                  }))} onBobberStopDepthChange={bobberStopDepth => setState(s => ({
                    ...s,
                    bobberStopDepth
                  }))} presets={presets} activePreset={activePreset} onPresetSave={i => handlePresetClick(i)} data-id="element-64" />

                      {/* Expand to Fullscreen Button - Bottom Right */}
                      <button onClick={() => {
                    setIsFullScreen(true);
                    setZoomLevel(0.6);
                  }} className="absolute bottom-1 right-1 z-40 w-6 h-6 flex items-center justify-center rounded-md bg-white/90 backdrop-blur-sm border border-gray-200 text-gray-900 shadow-md hover:bg-white active:scale-95 transition-all md:hidden" data-id="element-65">
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" data-id="element-66">
                          <path d="M1 5V1H5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-67" />
                          <path d="M15 11V15H11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-68" />
                          <path d="M15 5V1H11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-69" />
                          <path d="M1 11V15H5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-70" />
                        </svg>
                      </button>
                    </div>
                    {/* Control Panel - Bottom */}
                    <div className="shrink-0 overflow-auto max-h-[40%] -mt-px" data-id="element-71">
                      <ControlPanel state={state} setState={setState} onToggleFullScreen={() => {
                    setIsFullScreen(true);
                    setZoomLevel(0.6);
                  }} onProgramWithAI={() => setShowAIProgram(true)} zoomLevel={zoomLevel} data-id="element-72" />
                    </div>
                  </>}
              </div>
            </div>
          </div>
        </div>
      </main>
      <a href="http://www.baitmoverpro.com" target="_blank" rel="noopener noreferrer" className="text-center text-sm text-blue-400 hover:text-blue-300 underline py-2 block" data-id="element-73">
        www.baitmoverpro.com
      </a>

      {/* AI Program Overlay */}
      {showAIProgram && <AIProgramPage onClose={() => setShowAIProgram(false)} data-id="element-74" />}
    </div>;
}